import { useEffect } from 'react';
import { Box, Button, DialogContent, FormControl, IconButton, Stack } from '@mui/material';
import InputLabel from 'components/InputLabel';
import { Controller, SubmitHandler, useForm, type UseFormHandleSubmit } from 'react-hook-form';
import RoundedMultilineInput from 'components/RoundedMultilineInput';
import { useTranslation } from 'react-i18next';
import Choices from 'features/quizzes/components/quiz-form/Choices';
import SettingControl from 'components/SettingControl';
import { Switch } from 'components/Switch';
import * as z from 'zod';
import Icon from 'components/Icon';
import { zodResolver } from '@hookform/resolvers/zod';
import { quizQuestionSchema } from 'features/quizzes/validation/quizSchema';
import { POLLS_INPUT_MAX_LENGTH } from 'features/polls/constants';

export type QuizQuestionFormInput = z.infer<typeof quizQuestionSchema>;

type QuizQuestionFormProps = {
  defaultValues?: QuizQuestionFormInput;
  questionNumber: number;
  totalQuestions: number;
  onAddQuestion: SubmitHandler<QuizQuestionFormInput>;
  onNext: SubmitHandler<QuizQuestionFormInput>;
  onBack: SubmitHandler<QuizQuestionFormInput>;
  onDelete: () => void;
  renderDialogActions: (state: {
    isValid: boolean;
    handleSubmit: UseFormHandleSubmit<QuizQuestionFormInput>;
  }) => React.ReactNode;
};

export const newQuestionDefaultValues = {
  text: '',
  multipleChoices: false,
  choices: [
    { text: '', correct: false },
    { text: '', correct: false },
  ],
};

const QuizQuestionForm = ({
  defaultValues,
  questionNumber,
  totalQuestions,
  onAddQuestion,
  onNext,
  onBack,
  onDelete,
  renderDialogActions,
}: QuizQuestionFormProps) => {
  const { t } = useTranslation('quizzes');

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(quizQuestionSchema),
    mode: 'onChange',
    defaultValues: defaultValues ?? newQuestionDefaultValues,
  });

  const multipleChoices = watch('multipleChoices');
  const choices = watch('choices');

  // Reset form when defaultValues change (e.g., after deletion)
  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  // Reset choice selections when switching from multiple choice to single choice
  // if more than 1 choice is selected
  useEffect(() => {
    if (!multipleChoices) {
      const checkedCount = choices.filter((choice) => choice.correct).length;

      if (checkedCount > 1) {
        choices.forEach((_, index) => {
          setValue(`choices.${index}.correct`, false, {
            shouldValidate: true,
          });
        });
      }
    }
  }, [multipleChoices, choices, setValue]);

  const isOnLastQuestion = questionNumber === totalQuestions;
  const hasQuestionsAhead = questionNumber < totalQuestions;

  return (
    <>
      <DialogContent
        sx={{
          pt: 0,
        }}
      >
        <Stack spacing={6}>
          <Box
            sx={{
              position: 'relative',
            }}
          >
            <FormControl
              fullWidth
              error={!!errors?.text}
              sx={{
                '& .MuiInputBase-root': {
                  marginTop: '32px',
                },
              }}
            >
              <InputLabel htmlFor="quiz-form-question">
                {t('quiz_form.questions.question_label', {
                  number: questionNumber,
                })}
              </InputLabel>
              <Controller
                name="text"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <RoundedMultilineInput
                    id="quiz-form-question"
                    autoFocus
                    inputProps={{
                      maxLength: POLLS_INPUT_MAX_LENGTH,
                    }}
                    {...field}
                    inputRef={ref}
                  />
                )}
              />
            </FormControl>
            <Box
              sx={{
                position: 'absolute',
                right: -10,
                top: -10,
              }}
            >
              <IconButton
                onClick={onDelete}
                aria-label={t('cross_aria_label')}
                sx={[
                  {
                    fontSize: '32px',
                  },
                ]}
              >
                <Icon name="close" />
              </IconButton>
            </Box>
          </Box>

          <SettingControl
            label={t('quiz_form.questions.multiple_answers.label')}
            control={
              <Controller
                name="multipleChoices"
                control={control}
                render={({ field: { value, ref, ...field } }) => (
                  <Switch
                    {...field}
                    slotProps={{
                      input: {
                        ref,
                        'aria-label': value
                          ? t('quiz_form.questions.multiple_answers.switch_disable_aria_label')
                          : t('quiz_form.questions.multiple_answers.switch_enable_aria_label'),
                      },
                    }}
                    checked={value}
                  />
                )}
              />
            }
          />

          <Choices
            errors={errors}
            control={control}
            watch={watch}
            setValue={setValue}
            multipleChoices={multipleChoices}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Button
              startIcon={<Icon name="back" />}
              type="button"
              variant="outlined"
              onClick={handleSubmit(onBack)}
              color="grayscale"
              disabled={!isValid}
            >
              {t('quiz_form.btn_prev_question')}
            </Button>

            <Box sx={{ marginLeft: 'auto' }}>
              {hasQuestionsAhead && (
                <Button
                  endIcon={<Icon name="chevron-right" />}
                  type="button"
                  variant="outlined"
                  onClick={handleSubmit(onNext)}
                  disabled={!isValid}
                >
                  {t('quiz_form.btn_next_question')}
                </Button>
              )}

              {isOnLastQuestion && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleSubmit(onAddQuestion)}
                  disabled={!isValid}
                >
                  {t('quiz_form.btn_add_question')}
                </Button>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      {renderDialogActions &&
        renderDialogActions({
          isValid,
          handleSubmit,
        })}
    </>
  );
};

export default QuizQuestionForm;
